<template>
  <main-layout>
    <div class="image"></div>
    <div class="rocket"></div>

    <template v-slot:top-section>
      <top-section-wrapper class="pb-40">
        <top-section-content hide-image>
          <template v-slot:top-section-heading>
            <FontTitle text="IPO Board" class="title"></FontTitle>
          </template>
          <template v-slot:top-section-sub-heading>
            The score on the IPO Board depends on your investments in IPO
            projects. The higher your ranking, the better IPO deals you can
            have.
          </template>
        </top-section-content>
      </top-section-wrapper>
    </template>
    <div class="leaderboard-wrapper">
      <div class="leaderboard-my-score">
        <div class="leaderboard-my-score-title">My Score</div>
        <div class="leaderboard-my-score-block">
          <div class="leaderboard-my-score-block-name">
            Rank:<span>{{ currentUserIndex }}</span>
          </div>
          <div class="leaderboard-my-score-block-name">
            Address:<span>
              {{ trimMiddle(currentUserIpoLeaderboard?.erc20Address) }}
            </span>
          </div>
          <div
            v-for="metric in myScoreMetrics"
            :key="metric.label"
            class="leaderboard-my-score-block-name"
          >
            {{ metric.label }}:
            <tooltip-custom
              :content="metric.tooltipValue"
            >
              <span style="margin-left: 0">{{ metric.displayValue }}</span>
            </tooltip-custom>
          </div>
          <div class="leaderboard-my-score-squares-one"></div>
          <div class="leaderboard-my-score-squares-second"></div>
        </div>
      </div>
      <div class="leaderboard">
        <div class="leaderboard-title">IPO Board</div>
        <table class="leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Address</th>
              <th>Total Score</th>
            </tr>
          </thead>
        </table>
        <table class="leaderboard-table">
          <tbody>
            <tr
              v-for="(user, index) in ipoLeaderboard"
              :key="user.erc20Address"
              :class="{
                'current-user':
                  user.erc20Address === userProfile.userERCAddress,
              }"
            >
              <td>{{ index + 1 }}</td>
              <td class="address">
                {{ user?.erc20Address ? trimMiddle(user.erc20Address) : "" }}
              </td>
              <td>
                <tooltip-custom
                  :content="(user.totalScore || 0).toString()"
                >
                  {{ getCorrectScore(user.totalScore || 0) }}
                </tooltip-custom>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main-layout>
</template>
<script>
import TopSectionWrapper from "@/components/TopSectionWrapper";
import MainLayout from "@/layouts/main";
import TopSectionContent from "@/components/TopSectionContent";
import { mapState } from "vuex";
import { trimMiddle } from "@/helpers";
import TooltipCustom from "@/components/TooltipCustom";
import FontTitle from "@/components/FontTitle";

export default {
  name: "IpoBoardPage",
  components: {
    FontTitle,
    TopSectionWrapper,
    MainLayout,
    TopSectionContent,
    TooltipCustom,
  },

  created() {
    this.$store.dispatch("fetchIpoLeaderboard");
  },

  computed: {
    ...mapState(["ipoLeaderboard", "currentUserIpoLeaderboard", "userProfile"]),
    currentUserIndex() {
      if (this.currentUserIpoLeaderboard?.rank) {
        return this.currentUserIpoLeaderboard.rank;
      }

      const index = this.ipoLeaderboard?.findIndex(
        (user) => user.erc20Address === this.userProfile?.userERCAddress
      );

      return index >= 0 ? index + 1 : this.ipoLeaderboard.length;
    },
    myScoreMetrics() {
      const metrics = [
        ["Old Score", "oldScore"],
        ["First Option", "firstOptionPoints", false, "option"],
        ["Second Option", "secondOptionPoints", false, "secondOption"],
        ["Pre-IPO Projects", "preIpoProjects", true],
        ["Pre-IPO Invested", "preIpoInvested"],
        ["Pre-IPO Refunded", "preIpoRefunded"],
        ["IPO Projects", "ipoProjects", true],
        ["IPO Invested", "ipoInvested"],
        ["IPO Refunded", "ipoRefunded"],
        ["Referral Invested", "referralInvested"],
        ["Referral Points", "referralPoints", false, "fixedTwoDecimals"],
        ["Total Score", "totalScore"],
      ];

      return metrics.map(([label, key, isInteger, format]) => {
        const value = Number(this.currentUserIpoLeaderboard?.[key] || 0);
        const appliedSecondOption = Number(
          this.currentUserIpoLeaderboard?.appliedSecondOptionPoints || 0
        );

        let displayValue = isInteger ? value : this.getCorrectScore(value);
        let tooltipValue = value.toString();

        if (format === "fixedTwoDecimals") {
          displayValue = value.toLocaleString("en-US", {
            minimumFractionDigits: value === 0 ? 0 : 2,
            maximumFractionDigits: 2,
            useGrouping: false,
          });
          tooltipValue = displayValue;
        } else if (format === "option") {
          displayValue = this.getCorrectScore(value);
        } else if (format === "secondOption") {
          displayValue = `${this.getCorrectScore(value)} (${this.getCorrectScore(
            appliedSecondOption
          )})`;
          tooltipValue = `${value} (${appliedSecondOption})`;
        }

        return {
          label,
          value,
          tooltipValue,
          displayValue,
        };
      });
    },
  },
  methods: {
    trimMiddle,
    getCorrectScore(score) {
      return parseFloat(Number(score).toFixed(2));
    },
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/styles/mixins";
:deep(.popper) {
  --popper-theme-background-color: #000000;
  --popper-theme-background-color-hover: #000000;
  --popper-theme-text-color: #ffffff;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 6px;
  --popper-theme-padding: 5px;
  --popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
  width: fit-content !important;
  text-align: center;
  font-size: 16px;
}
.current-user {
  background-color: #a4c7a4;
}

.leaderboard-wrapper {
  position: relative;
  display: flex;
  justify-content: space-between;
  column-gap: 40px;
  margin: 250px auto 0;
  font-family: "Space Mono", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 36px;
  letter-spacing: -0.05em;

  color: #000000;

  @include respond-below(lg) {
    font-size: 14px;
    line-height: 21px;
    max-width: 840px;
    margin: 0 25px;

    min-width: initial;
    width: 100%;
    overflow-x: auto;
    display: flex;
    flex-direction: column;
    row-gap: 50px;
  }
  @include respond-to(md) {
    max-width: 800px;
  }
  @include respond-to(xs) {
    margin: 0;
  }
}
.leaderboard-my-score {
  position: relative;
  flex: 1;
  min-width: 0;

  @include respond-below(lg) {
    flex: none;
    width: 100%;
  }
  &-title {
    font-family: "Space Mono";
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 114.5%;
    letter-spacing: -0.04em;

    color: #262626;
    margin-bottom: 15px;
  }

  &-block {
    position: relative;
    display: flex;
    flex-direction: column;
    row-gap: 17px;
    background: #fafafa;
    border: 1px solid #000000;
    width: 100%;
    box-sizing: border-box;
    padding: 28px 34px;

    &-name {
      font-family: "Space Mono";
      font-style: normal;
      font-weight: 400;
      font-size: 20px;
      line-height: 114.5%;
      letter-spacing: -0.04em;

      color: #000000;
      display: flex;
      flex-wrap: wrap;
      column-gap: 12px;
      overflow-wrap: anywhere;
      span {
        font-weight: 600;
        margin-left: 0;
      }
    }
  }
  &-squares-one {
    position: absolute;
    background-image: url("../assets/squares.svg");
    bottom: -18px;
    left: -25px;
    width: 224px;
    height: 208px;
    z-index: -1;
  }
  &-squares-second {
    position: absolute;
    background-image: url("../assets/squares.svg");
    bottom: -14px;
    left: -21px;
    width: 224px;
    height: 208px;
    z-index: -1;
  }
}
.leaderboard {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-width: 700px;
  @include respond-to(sm) {
    min-width: 600px;
  }
  @include respond-to(xs) {
    min-width: 300px;
  }
  &-title {
    font-family: "Space Mono";
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 114.5%;
    letter-spacing: -0.04em;

    color: #262626;
    margin-bottom: 15px;
  }
  .leaderboard-table {
    border-collapse: collapse;
    margin: 0 auto;
    min-width: 800px;
    overflow: hidden;
    z-index: 10;
    position: relative;

    @include respond-to(sm) {
      min-width: 700px;
    }
    @include respond-to(xs) {
      min-width: 300px;
      max-width: 700px;
      width: 100%;
    }
    thead {
      background: #ffffff;
      border-top: 1px solid #000000;
      border-left: 1px solid #000000;
      border-right: 1px solid #000000;

      height: 51px;

      th {
        font-weight: 400;
        font-size: 20px;
        line-height: 30px;
        letter-spacing: -0.05em;
        color: #272727;
        font-style: normal;
        width: 170px;

        @include respond-to(xs) {
          font-size: 16px;
          padding: 0 25px;
        }
      }
    }

    tbody {
      background: #e5e7eb;
      border: 1px solid #000000;
      font-family: "Space Mono";
      font-style: normal;
      font-weight: 400;
      font-size: 18px;
      line-height: 27px;
      align-items: center;
      text-align: center;
      letter-spacing: -0.05em;

      color: #272727;
      display: block;
      td {
        text-align: center;
        font-weight: normal;
        position: relative;
        width: 16%;
      }

      tr:not(.sep) {
        height: 44px;
      }
    }
  }
}
.title {
  font-family: "Space Mono";
  font-style: normal;
  font-weight: 700;
  font-size: 58px;
  line-height: 114.5%;
  letter-spacing: -0.006em;
  @include respond-to(xs) {
    font-size: 40px;
  }
}
.image {
  position: absolute;
  background-image: url("../assets/clouds-rocket.png");
  background-size: cover;
  height: 865px;
  top: -400px;
  width: 97%;
  z-index: 0;
  background-position-x: 0%;
  background-position-y: -19px;
  overflow: hidden;
  white-space: nowrap;
  @include respond-to(sm) {
    background-position-x: 40%;
  }
  @include respond-to(xs) {
    background-position-x: 25%;
  }
}
.rocket {
  position: absolute;
  background-image: url("../assets/rocket.png");
  width: 73px;
  height: 149px;
  left: 61.6%;
  top: -185px;
  z-index: 0;
  @include respond-to(md) {
    left: 76%;
  }
  @include respond-to(sm) {
    left: 67.6%;
  }
  @include respond-to(xs) {
    left: 85.6%;
  }
  @media (max-width: 700px) {
    display: none;
  }
}
</style>
